import { createContext, useCallback, useContext } from "react";

import { Permissions } from "./constants";

import { useGetPermissions } from "@/queries/rbac";
import { EntityType } from "@/queries/folder-entities/types";

export interface RbacContextValue {
  permissions: Permissions[] | undefined;
  isCreateDisabled: (itemType: EntityType) => boolean;
  isEditDisabled: (itemType: EntityType) => boolean;
  isDeleteDisabled: (itemType: EntityType) => boolean;
  isDeployDisabled: (itemType: EntityType) => boolean;
}

export const RbacContext = createContext<RbacContextValue | undefined>(undefined);

export const RbacProvider = ({ children }: { children: React.ReactNode }) => {
  const { data } = useGetPermissions();

  const isCreateDisabled = useCallback(
    (itemType: EntityType) => {
      switch (itemType) {
        case EntityType.PROMPT:
        case EntityType.SNIPPET:
          return !data?.permissions?.includes(Permissions.PROMPT_CREATE);
        case EntityType.WORKFLOW:
          return !data?.permissions?.includes(Permissions.WORKFLOW_CREATE);
        case EntityType.DATASET:
          return !data?.permissions?.includes(Permissions.DATASET_CREATE);
        case EntityType.REPORT:
        case EntityType.SMART_TABLE:
          return !data?.permissions?.includes(Permissions.REPORT_CREATE);
        case EntityType.TOOL:
          return !data?.permissions?.includes(Permissions.TOOL_CREATE);
        default:
          return false;
      }
    },
    [data?.permissions],
  );

  const isEditDisabled = useCallback(
    (itemType: EntityType) => {
      switch (itemType) {
        case EntityType.PROMPT:
        case EntityType.SNIPPET:
          return !data?.permissions?.includes(Permissions.PROMPT_EDIT);
        case EntityType.WORKFLOW:
          return !data?.permissions?.includes(Permissions.WORKFLOW_EDIT);
        case EntityType.DATASET:
          return !data?.permissions?.includes(Permissions.DATASET_EDIT);
        case EntityType.REPORT:
        case EntityType.SMART_TABLE:
          return !data?.permissions?.includes(Permissions.REPORT_EDIT);
        case EntityType.TOOL:
          return !data?.permissions?.includes(Permissions.TOOL_EDIT);
        default:
          return false;
      }
    },
    [data?.permissions],
  );

  const isDeleteDisabled = useCallback(
    (itemType: EntityType) => {
      switch (itemType) {
        case EntityType.PROMPT:
        case EntityType.SNIPPET:
          return !data?.permissions?.includes(Permissions.PROMPT_DELETE);
        case EntityType.WORKFLOW:
          return !data?.permissions?.includes(Permissions.WORKFLOW_DELETE);
        case EntityType.DATASET:
          return !data?.permissions?.includes(Permissions.DATASET_DELETE);
        case EntityType.REPORT:
        case EntityType.SMART_TABLE:
          return !data?.permissions?.includes(Permissions.REPORT_DELETE);
        case EntityType.TOOL:
          return !data?.permissions?.includes(Permissions.TOOL_DELETE);
        default:
          return false;
      }
    },
    [data?.permissions],
  );

  const isDeployDisabled = useCallback(
    (itemType: EntityType) => {
      switch (itemType) {
        case EntityType.PROMPT:
          return !data?.permissions?.includes(Permissions.PROMPT_DEPLOY);
        case EntityType.WORKFLOW:
          return !data?.permissions?.includes(Permissions.WORKFLOW_DEPLOY);
        default:
          return false;
      }
    },
    [data?.permissions],
  );

  return (
    <RbacContext.Provider
      value={{
        permissions: data?.permissions,
        isCreateDisabled,
        isEditDisabled,
        isDeleteDisabled,
        isDeployDisabled,
      }}
    >
      {children}
    </RbacContext.Provider>
  );
};

export const useRbac = () => {
  const context = useContext(RbacContext);
  if (!context) {
    throw new Error("useRbac must be used within a RbacProvider");
  }
  return context;
};
